import { AppInitializationProvider } from '@/providers/app-initialization/app-initialization.provider';
import { TrackingService } from '@/providers/tracking/tracking.service';
import { jsonToB64 } from '@/shared/utils/angular-sdk';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { LocationStrategy } from '@angular/common';
import { inject, Injectable, Injector } from '@angular/core';
import { ChildActivationEnd, NavigationEnd, type NavigationExtras, Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom, type Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import type { ClassificationNodeId } from '../../interfaces/Classification.interface';
import { BuildingsProvider } from '../../providers/buildings/buildings.provider';
import { DashboardingProvider } from '../../providers/dashboarding.provider';
import { ProfileProvider } from '../../providers/profile/profile.provider';
import { CREATE_BUILDING_RIGHT_ACCESS } from '../../providers/rights.constants';
import { StateStatusProvider } from '../../providers/state-status/state-status.provider';
import { Page, type PageParams, type RoutingService } from './routing.service';

type RoutePaths = {
    [P in Page]: {
        generator: (params: PageParams<P>) => {
            url: string[];
            query?: Record<string, string>;
        };
        descriptor: string;
        descriptors?: string[];
    };
};

export const routePaths: RoutePaths = {
    [Page.LOGIN]: {
        generator: _ => ({
            url: ['login']
        }),
        descriptor: 'login'
    },
    [Page.BUILDINGS]: {
        generator: _ => ({
            url: ['buildings']
        }),
        descriptor: 'buildings'
    },
    [Page.BUILDING_DETAILS]: {
        generator: ({ buildingId }) => ({
            url: ['buildings', buildingId]
        }),
        descriptor: 'buildings/:buildingId',
        descriptors: [
            'buildings/:buildingId/general',
            'buildings/:buildingId/attributes',
            'buildings/:buildingId/contacts',
            'buildings/:buildingId/automate',
            'buildings/:buildingId/users',
            'buildings/:buildingId/operating-schedule'
        ]
    },
    [Page.METER_DETAILS]: {
        generator: ({ meterId }) => ({
            url: ['meters', 'edition', meterId]
        }),
        descriptor: 'meters/edition/:meterId',
        descriptors: ['meters/edition/:meterId/general', 'meters/edition/:meterId/attributes', 'meters/edition/:meterId/distributor']
    },
    [Page.DELIVERY_POINTS]: {
        generator: _ => ({
            url: ['delivery-points']
        }),
        descriptor: 'delivery-points'
    },
    [Page.BILLS_BY_DELIVERY_POINT]: {
        generator: ({ deliveryPointUid }) => ({
            url: ['bills', 'delivery-points', deliveryPointUid]
        }),
        descriptor: 'bills/delivery-points/:deliveryPointId'
    },
    [Page.BILLING_LIST]: {
        generator: ({ deliveryPointIds }) => ({
            url: ['billing-list'],
            query: {
                deliveryPointIds: JSON.stringify(deliveryPointIds)
            } as Record<string, string>
        }),
        descriptor: 'billing-list'
    },
    [Page.SETTINGS]: {
        generator: _ => ({
            url: ['me', 'settings']
        }),
        descriptor: 'me/settings'
    },
    [Page.USER_PROFILE]: {
        generator: _ => ({
            url: ['me', 'profile']
        }),
        descriptor: 'me/profile'
    },
    [Page.METERS_LIST]: {
        generator: ({ buildingUid }) => ({
            url: ['manual-readings', buildingUid]
        }),
        descriptor: 'manual-readings/:buildingId'
    },
    [Page.MAPS]: {
        generator: _ => ({
            url: ['maps']
        }),
        descriptor: 'maps'
    },
    [Page.USERS_PROFILES_MANAGEMENT]: {
        generator: _ => ({
            url: ['admin-users-profiles']
        }),
        descriptor: 'admin-users-profiles'
    },
    [Page.IMPORT_HISTORY]: {
        generator: ({ resourceTypeInitialFilters }) => ({
            url: ['admin-import-history'],
            query: {
                resourceTypeInitialFilters: resourceTypeInitialFilters ? JSON.stringify(resourceTypeInitialFilters) : undefined
            } as Record<string, string>
        }),
        descriptor: 'admin-import-history'
    },
    [Page.PROJECT_SETTINGS]: {
        generator: _ => ({
            url: ['admin-settings']
        }),
        descriptor: 'admin-settings'
    },
    [Page.ATTRIBUTES_MANAGEMENT]: {
        generator: _ => ({
            url: ['attribute-management']
        }),
        descriptor: 'attribute-management'
    },
    [Page.IPMVP]: {
        generator: _ => ({
            url: ['admin-ipmvp']
        }),
        descriptor: 'admin-ipmvp'
    },
    [Page.BUSINESS_INDICATORS_DETAILS]: {
        generator: _ => ({
            url: ['business-indicators', 'details']
        }),
        descriptor: 'business-indicators/details'
    },
    [Page.BUSINESS_INDICATORS_MANAGEMENT]: {
        generator: _ => ({
            url: ['admin', 'business-indicators']
        }),
        descriptor: 'admin/business-indicators'
    },
    [Page.BUSINESS_INDICATOR_EDITION]: {
        generator: ({ businessIndicatorId }) => ({
            url: ['admin', 'business-indicators', businessIndicatorId, 'details']
        }),
        descriptor: 'admin/business-indicators/:businessIndicatorId/details'
    },
    [Page.BUSINESS_INDICATOR_REFERENCE_PERIODS_EDITION]: {
        generator: ({ businessIndicatorId }) => ({
            url: ['admin', 'business-indicators', businessIndicatorId, 'reference-period']
        }),
        descriptor: 'admin/business-indicators/:businessIndicatorId/reference-period'
    },
    [Page.CLASSIFICATIONS_MANAGEMENT]: {
        generator: _ => ({
            url: ['classifications']
        }),
        descriptor: 'classifications'
    },
    [Page.ACTION_CONSULTATION]: {
        generator: ({ actionId }) => ({
            url: ['actions', actionId, 'edition']
        }),
        descriptor: 'actions/:actionId/edition'
    },
    [Page.ACTIONS_LIST]: {
        generator: _ => ({
            url: ['actions']
        }),
        descriptor: 'actions'
    },
    [Page.RECOMMENDED_ACTIONS_LIST]: {
        generator: _ => ({
            url: ['recommended-actions', 'to-validate']
        }),
        descriptor: 'recommended-actions/to-validate'
    },
    [Page.DASHAVIZ]: {
        generator: _ => ({
            url: ['dashaviz']
        }),
        descriptor: 'dashaviz'
    },
    [Page.DATA_EXPORT]: {
        generator: _ => ({
            url: ['data-export']
        }),
        descriptor: 'data-export'
    },
    [Page.DOCUMENT_MANAGEMENT_IMPORT]: {
        generator: _ => ({
            url: ['document-management-import']
        }),
        descriptor: 'document-management-import'
    },
    [Page.BUILDING_CREATION]: {
        generator: _ => ({
            url: ['buildings', 'building-creation']
        }),
        descriptor: 'buildings/building-creation'
    },
    [Page.INDICATORS_BUILDINGS_COMPARISON]: {
        generator: ({ tab }) => ({
            url: ['indicators-buildings-comparison', tab]
        }),
        descriptor: 'indicators-buildings-comparison/:tab'
    },
    [Page.ACCOUNT_CREATION]: {
        generator: () => ({
            url: ['account-creation']
        }),
        descriptor: 'account-creation'
    },
    [Page.CONFIRM_PASSWORD]: {
        generator: ({ email, token }) => ({
            url: ['account', 'reset-password', email, token]
        }),
        descriptor: 'account/reset-password/:email/:token'
    },
    [Page.DATA_HEALTH_SYNTHESIS]: {
        generator: () => ({
            url: ['data-health', 'synthesis']
        }),
        descriptor: 'data-health/synthesis'
    },
    [Page.DATA_HEALTH_TEMPORAL_COMPLETENESS]: {
        generator: () => ({
            url: ['data-health', 'temporal-completeness']
        }),
        descriptor: 'data-health/temporal-completeness'
    },
    [Page.DATA_HEALTH_TEMPORAL_COMPLETENESS_DETAILS]: {
        generator: () => ({
            url: ['data-health', 'temporal-completeness', 'details']
        }),
        descriptor: 'data-health/temporal-completeness/details'
    },
    [Page.DATA_HEALTH_COMPLETENESS]: {
        generator: () => ({
            url: ['data-health', 'completeness']
        }),
        descriptor: 'data-health/completeness'
    },
    [Page.DATA_HEALTH_QUALITY]: {
        generator: () => ({
            url: ['data-health', 'quality']
        }),
        descriptor: 'data-health/quality'
    },
    [Page.DATA_QUALIFICATION_COMPLETENESS_DETAILS]: {
        generator: ({ timestep, metric, resourceType, resourceIds, universeIds, periodIds }) => ({
            url: ['data-quality-completeness-details'],
            query: {
                timestep,
                metric,
                resourceType,
                resourceIds: JSON.stringify(resourceIds),
                universeIds: JSON.stringify(universeIds),
                periodIds: JSON.stringify(periodIds)
            } as Record<string, string>
        }),
        descriptor: 'data-quality-completeness-details'
    },
    [Page.THRESHOLD_SCENARIO_LANDING]: {
        generator: _ => ({
            url: ['alerts', 'scenarios', 'threshold']
        }),
        descriptor: 'alerts/scenarios/threshold'
    },
    [Page.THRESHOLD_SCENARIO_EDITION]: {
        generator: ({ alertScenarioId }) => ({
            url: ['alerts', 'threshold', alertScenarioId, 'edit']
        }),
        descriptor: 'alerts/threshold/:alertScenarioId/edit'
    },
    [Page.THRESHOLD_SCENARIO_DETAILS]: {
        generator: ({ alertScenarioId, periodCode }) => ({
            url: ['alerts', 'threshold', alertScenarioId],
            query: {
                periodCode
            }
        }),
        descriptor: 'alerts/threshold/:alertScenarioId'
    },
    [Page.WATER_LEAKAGE_SCENARIO_LANDING]: {
        generator: _ => ({
            url: ['alerts', 'scenarios', 'water-leakage']
        }),
        descriptor: 'alerts/scenarios/water-leakage'
    },
    [Page.WATER_LEAKAGE_SCENARIO_EDITION]: {
        generator: ({ alertScenarioId }) => ({
            url: ['alerts', 'water-leakage', alertScenarioId, 'edit']
        }),
        descriptor: 'alerts/water-leakage/:alertScenarioId/edit'
    },
    [Page.DRIFT_SCENARIO_EDITION]: {
        generator: ({ alertScenarioId }) => ({
            url: ['alerts', 'drift', alertScenarioId, 'edit']
        }),
        descriptor: 'alerts/drift/:alertScenarioId/edit'
    },
    [Page.DRIFT_SCENARIO_DETAILS]: {
        generator: ({ alertScenarioId, periodCode }) => ({
            url: ['alerts', 'drift', alertScenarioId],
            query: { periodCode }
        }),
        descriptor: 'alerts/drift/:alertScenarioId'
    },
    [Page.WATER_LEAKAGE_SCENARIO_DETAILS]: {
        generator: ({ alertScenarioId, periodCode }) => ({
            url: ['alerts', 'water-leakage', alertScenarioId],
            query: {
                periodCode
            }
        }),
        descriptor: 'alerts/water-leakage/:alertScenarioId'
    },
    [Page.DRIFT_SCENARIO_LANDING]: {
        generator: _ => ({
            url: ['alerts', 'scenarios', 'drift']
        }),
        descriptor: 'alerts/scenarios/drift'
    },
    [Page.ALERT_SCENARIO_REPORT_PLANS]: {
        generator: ({ alertScenarioId }) => ({
            url: ['alerts', alertScenarioId, 'reports']
        }),
        descriptor: 'alerts/:alertScenarioId/reports'
    },

    [Page.DATA_VIEWER]: {
        generator: ({ tab }) => ({
            url: ['data-viewer', tab]
        }),
        descriptor: 'data-viewer/:tab',
        descriptors: [
            'data-viewer/sensors',
            'data-viewer/invoices',
            'data-viewer/yearlyCalendarData',
            'data-viewer/billing',
            'data-viewer/manualIndexReading'
        ]
    },
    [Page.INVOICE_EDITION]: {
        generator: ({ invoiceId }) => ({
            url: ['invoices', invoiceId]
        }),
        descriptor: 'invoices/:invoiceId'
    },
    [Page.SPECIFIC_MANUAL_INDEX_READINGS]: {
        generator: ({ meterIds }) => ({
            url: ['specific-manual-index-readings'],
            query: {
                meterIds: JSON.stringify(meterIds)
            } as Record<string, string>
        }),
        descriptor: 'specific-manual-index-readings'
    },
    [Page.METERS_INDEX_STATEMENT]: {
        generator: ({ meterId }) => ({
            url: ['meter-index', meterId, 'statement']
        }),
        descriptor: 'meter-index/:meterId/statement'
    },
    [Page.BILLS_IMPORT_FILES]: {
        generator: _ => ({
            url: ['bills-import-files']
        }),
        descriptor: 'bills-import-files'
    },
    [Page.METER_INDEX_INIT]: {
        generator: ({ meterId }) => ({
            url: ['meter-index', meterId, 'init']
        }),
        descriptor: 'meter-index/:meterId/init'
    },
    [Page.DASHBOARDS]: {
        generator: _ => ({
            url: ['building-home-dashboard']
        }),
        descriptor: 'building-home-dashboard'
    },
    [Page.BILLS_BY_BUILDING]: {
        generator: ({ dashboardRight, indicatorCode }) => ({
            url: ['bills', 'buildings', dashboardRight, indicatorCode]
        }),
        descriptor: 'bills/buildings/:dashboardRight/:indicatorCode'
    },
    [Page.BILLS_BY_BUILDING_ANALYSIS]: {
        generator: ({ buildingId, universeId }) => ({
            url: ['bills', 'buildings', buildingId, 'analysis', universeId]
        }),
        descriptor: 'bills/buildings/:buildingId/analysis/:universeId'
    },
    [Page.DELIVERY_POINT_ADD]: {
        generator: _ => ({
            url: ['delivery-point', 'add']
        }),
        descriptor: 'delivery-point/add'
    },
    [Page.CONNECTED_AS]: {
        generator: _ => ({
            url: ['connect']
        }),
        descriptor: 'connect'
    },
    [Page.IPE_HISTORY]: {
        generator: ({ buildingId, tileId }) => ({
            url: ['ipe-history', tileId],
            query: {
                buildingId
            } as Record<string, string>
        }),
        descriptor: 'ipe-history/:tileId'
    },
    [Page.SUPPLIER_PORTAL_ACCESS]: {
        generator: _ => ({
            url: ['supplier-portal-access']
        }),
        descriptor: 'supplier-portal-access'
    },
    [Page.DOCUMENT_MANAGEMENT]: {
        generator: _ => ({
            url: ['document-management']
        }),
        descriptor: 'document-management'
    },
    [Page.CONTRACT_POWER_OPTIMIZATION]: {
        generator: _ => ({
            url: ['contract-power-optimization']
        }),
        descriptor: 'contract-power-optimization'
    },
    [Page.SUPPLIERS_MISSING_INVOICES]: {
        generator: _ => ({
            url: ['suppliers-missing-invoices']
        }),
        descriptor: 'suppliers-missing-invoices'
    },
    [Page.BUDGET_EDITION]: {
        generator: _ => ({
            url: ['budget-edition']
        }),
        descriptor: 'budget-edition'
    },
    [Page.BUDGET_FUNNEL_VIEW]: {
        generator: ({ dashboardRight, timeseriesIndicator }) => ({
            url: ['funnel-view', dashboardRight, timeseriesIndicator]
        }),
        descriptor: 'funnel-view/:dashboardRight/:timeseriesIndicator'
    },
    [Page.CUSTOM_TILE_CONFIGURATION]: {
        generator: ({ tileGroup }) => ({
            url: ['tile-creation-menu', tileGroup]
        }),
        descriptor: 'tile-creation-menu/:tileGroup'
    },
    [Page.MULTI_SENSOR_TILE_CREATION]: {
        generator: ({ tileGroup }) => ({
            url: ['tile-creation-multi-sensor', tileGroup]
        }),
        descriptor: 'tile-creation-multi-sensor/:tileGroup'
    },
    [Page.BILLS_HOME]: {
        generator: _ => ({
            url: ['invoice']
        }),
        descriptor: 'invoice'
    },
    [Page.INVOICE_ERROR]: {
        generator: ({ invoiceReference, supplierId }) => ({
            url: ['invoice', 'error', invoiceReference, supplierId]
        }),
        descriptor: 'invoice/error/:invoiceReference/:supplierId'
    },
    [Page.INVOICE_DISCREPANCY]: {
        generator: _ => ({
            url: ['invoice-discrepancy']
        }),
        descriptor: 'invoice-discrepancy'
    },
    [Page.NO_BUILDING_ACCESS_ERROR]: {
        generator: _ => ({
            url: ['no-building-access']
        }),
        descriptor: 'no-building-access'
    },
    [Page.TEST_DATASOURCE]: {
        generator: _ => ({
            url: ['test-datasource']
        }),
        descriptor: 'test-datasource'
    },
    [Page.TEST_DASHBOARD]: {
        generator: _ => ({
            url: ['dashboard-test']
        }),
        descriptor: 'dashboard-test'
    },
    [Page.MANUAL_INDEX_READINGS]: {
        generator: _ => ({
            url: ['manual-index-readings']
        }),
        descriptor: 'manual-index-readings'
    },
    [Page.AUTOMATED_REPORTS_LIST]: {
        generator: _ => ({
            url: ['reports']
        }),
        descriptor: 'reports'
    },
    [Page.AUTOMATED_REPORT_EDITION]: {
        generator: ({ reportPlan }) => ({
            url: ['reports', 'edition'],
            query: {
                reportPlan: JSON.stringify(reportPlan)
            } as Record<string, string>
        }),
        descriptor: 'reports/edition'
    },
    [Page.SYSTEM_EDITION]: {
        generator: ({ buildingUid, systemUid }) => ({
            url: ['readings', buildingUid, systemUid, 'edit']
        }),
        descriptor: 'readings/:buildingUid/:systemUid/edit'
    },
    [Page.VIRTUAL_SYSTEM_EDITION]: {
        generator: ({ buildingUid, systemUid }) => ({
            url: ['virtual-system-edition', buildingUid, systemUid]
        }),
        descriptor: 'virtual-system-edition/:buildingUid/:systemUid'
    },
    [Page.VIRTUAL_SENSOR_EDITION]: {
        generator: ({ buildingUid, sensorId, systemUid }) => ({
            url: ['virtual-sensor-edition'],
            query: {
                buildingUid,
                systemUid,
                sensorId
            } as Record<string, string>
        }),
        descriptor: 'virtual-sensor-edition'
    },
    [Page.SYSTEM_ADD]: {
        generator: _ => ({
            url: ['readings', 'add']
        }),
        descriptor: 'readings/add'
    },
    [Page.VIRTUAL_SYSTEM_ADD]: {
        generator: ({ system }) => ({
            url: ['virtual-system-add'],
            query: {
                system: JSON.stringify(system)
            } as Record<string, string>
        }),
        descriptor: 'virtual-system-add'
    },
    [Page.SYSTEM_ADD_ADVANCED]: {
        generator: ({ system }) => ({
            url: ['system-add-advanced'],
            query: {
                system: JSON.stringify(system)
            } as Record<string, string>
        }),
        descriptor: 'system-add-advanced'
    },
    [Page.ENTITY_MANAGEMENT]: {
        generator: _ => ({
            url: ['managed-entity']
        }),
        descriptor: 'managed-entity'
    },
    [Page.BASELOAD_ANALYSIS]: {
        generator: ({ filters, classificationNodeId, analysisKPI }) => ({
            url: ['baseload-analysis'],
            query: {
                classificationNodeId,
                filters: filters ? JSON.stringify(filters) : undefined,
                analysisKPI
            } as Record<string, string>
        }),
        descriptor: 'baseload-analysis'
    },
    [Page.BUILDING_BASELOAD_ANALYSIS]: {
        generator: ({ buildingId, date, analysisKPI }) => ({
            url: ['baseload-analysis', buildingId],
            query: {
                date: JSON.stringify(date),
                analysisKPI
            } as Record<string, string>
        }),
        descriptor: 'baseload-analysis/:buildingId'
    },
    [Page.CUSTOM_BUSINESS_INTELLIGENCE_ANALYSIS]: {
        generator: ({ filters, classificationNodeId }) => ({
            url: ['custom-business-intelligence-analysis'],
            query: {
                n: classificationNodeId,
                f: jsonToB64(filters)
            } as Record<string, string>
        }),
        descriptor: 'custom-business-intelligence-analysis'
    },
    [Page.DATABOARDS]: {
        generator: _ => ({
            url: ['databoards']
        }),
        descriptor: 'databoards'
    },
    [Page.EXPORT_LANDING]: {
        generator: _ => ({
            url: ['exports']
        }),
        descriptor: 'exports'
    },
    [Page.EXPORT_EDITION]: {
        generator: ({ appExportId }) => ({
            url: ['exports', 'edition'],
            query: {
                appExportId: JSON.stringify(appExportId)
            } as Record<string, string>
        }),
        descriptor: 'exports/edition'
    },
    [Page.GENERAL_CONDITIONS]: {
        generator: _ => ({
            url: ['general-conditions']
        }),
        descriptor: 'general-conditions'
    },
    [Page.PASSWORD_EXPIRED]: {
        generator: _ => ({
            url: ['password-expired']
        }),
        descriptor: 'password-expired'
    },
    [Page.HOMEPAGE]: {
        generator: _ => ({
            url: ['homepage']
        }),
        descriptor: 'homepage'
    },
    [Page.SEN_ANALYSIS_LANDING]: {
        generator: ({ universeIds }) => ({
            url: ['sen-analysis'],
            query: {
                universeIds: JSON.stringify(universeIds)
            } as Record<string, string>
        }),
        descriptor: 'sen-analysis'
    },
    [Page.SEN_ANALYSIS]: {
        generator: params => ({
            url: ['sen-analysis', 'analysis'],
            query:
                'analysisModelId' in params
                    ? {
                          analysisModelId: params.analysisModelId
                      }
                    : ({
                          timeseriesIds: JSON.stringify(params.timeseriesIds),
                          buildingId: params.buildingId
                      } as Record<string, string>)
        }),
        descriptor: 'sen-analysis/analysis'
    },
    [Page.DEET_EFA_LANDING]: {
        generator: _ => ({
            url: ['deet']
        }),
        descriptor: 'deet'
    },
    [Page.IOT_SUPERVISION]: {
        generator: _ => ({
            url: ['iot-supervision']
        }),
        descriptor: 'iot-supervision'
    },
    [Page.MIP]: {
        generator: _ => ({
            url: ['mip']
        }),
        descriptor: 'mip'
    },
    [Page.MIP_PLAN_EDIT]: {
        generator: ({ planId }) => ({
            url: ['mip', 'investment-plan', planId, 'edit']
        }),
        descriptor: 'mip/investment-plan/:planId/edit'
    },
    [Page.MIP_SCENARIO_SYNTHESIS]: {
        generator: ({ planId, scenarioId }) => ({
            url: ['mip', 'investment-plan', planId, 'scenario', scenarioId]
        }),
        descriptor: 'mip/investment-plan/:planId/scenario/:scenarioId'
    },
    [Page.ENERGY_SIGNATURE]: {
        generator: _ => ({
            url: ['energy-signature']
        }),
        descriptor: 'energy-signature'
    },
    [Page.THERMAL_COMFORT]: {
        generator: _ => ({
            url: ['thermal-comfort']
        }),
        descriptor: 'thermal-comfort'
    }
} as RoutePaths;

@Injectable({
    providedIn: 'root'
})
export class AngularRoutingService implements RoutingService {
    private _injector = inject(Injector);
    private _stateStatusProvider = inject(StateStatusProvider);
    private _buildingsProvider = inject(BuildingsProvider);
    private _profileProvider = inject(ProfileProvider);
    private _location = inject(LocationStrategy);
    private _appInitializationProvider = inject(AppInitializationProvider);
    private _trackingService = inject(TrackingService);

    private _routeReseted$ = new Subject<void>();
    private _navigationCount$ = new BehaviorSubject(0);

    private _routerIsInitialized$ = new BehaviorSubject(false);

    constructor() {
        firstValueFrom(this._appInitializationProvider.isAppInitialized$().pipe(filter(isInitialized => isInitialized))).then(() => {
            this._router$.next(this._injector.get(Router));
            this._currentPage$.subscribe(currentPage => {
                // avoid calling tracking service
                this._trackingService.pageView(currentPage);
            });
        });

        this._routerEvents$.pipe(filter((event: any) => event['url'])).subscribe(() => {
            this._routerIsInitialized$.next(true);
        });

        this._routerEvents$.pipe(filter(AngularRoutingService.isNavigationEndEvent)).subscribe(() => {
            this._navigationCount$.next(this._navigationCount$.getValue() + 1);
        });

        this._location.onPopState(() => {
            this._navigationCount$.next(Math.max(0, this._navigationCount$.getValue() - 2)); // -2 because it will get a +1 from the navigation end event
        });
    }

    private _router$ = new BehaviorSubject<Router | null>(null);
    private _routerEvents$ = this._router$.pipe(
        filter(isNotNullish),
        switchMap(router => router.events)
    );

    /**
     * @deprecated
     */
    private _currentPage$: Observable<Page> = this._router$.pipe(
        filter(isNotNullish),
        switchMap(router => {
            return router.events.pipe(
                filter(AngularRoutingService.isNavigationEndEvent),
                map(() => this.getCurrentPathDescriptor(router)),
                startWith(this.getCurrentPathDescriptor(router))
            );
        }),
        filter(path => !!path),
        map(activatedRoutePath => {
            return Object.entries(routePaths).find(
                ([_, path]) => path.descriptor === activatedRoutePath || path.descriptors?.find(d => d === activatedRoutePath)
            )?.[0] as Page | undefined;
        }),
        filter(isNotNullish),
        shareReplay({ refCount: false, bufferSize: 1 })
    );

    private getCurrentPathDescriptor(router: Router): string {
        let r = router.routerState.root;
        const activatedSnapshots = [r.snapshot];
        while (r.firstChild) {
            r = r.firstChild;
            activatedSnapshots.push(r.snapshot);
        }

        return activatedSnapshots.reduce((d, snapshot) => {
            if (!snapshot.routeConfig?.path) {
                return d;
            }
            return d + (d === '' ? '' : '/') + snapshot.routeConfig?.path;
        }, '');
    }

    private getRouter(): Router {
        return this._injector.get(Router);
    }

    /**
     * @deprecated Prefer using Router.navigate in feature services
     */
    goTo<P extends Page>(page: P, pageParams: PageParams<P>) {
        const dest = (routePaths[page].generator as any)(pageParams);

        return this.getRouter()
            .navigate(['/', ...dest.url], { queryParams: dest.query })
            .then(() => {});
    }

    goBack() {
        this._location.back();
    }

    /**
     * @deprecated
     */
    canGoBack$(): Observable<boolean> {
        return this._navigationCount$.pipe(map(count => count > 1));
    }

    /**
     * @deprecated
     */
    resetNavigationTo<P extends Page>(page: P, pageParams: PageParams<P>, extras?: NavigationExtras) {
        const dest = (routePaths[page].generator as any)(pageParams);

        this._navigationCount$.next(0);
        return this.getRouter()
            .navigate(['/', ...dest.url], { queryParams: dest.query, ...extras })
            .then(() => {});
    }

    /**
     * @deprecated
     */
    // The logic of this method should be extracted in guards
    async resetNavigationToHomePage(buildingId?: string) {
        Promise.all([this._stateStatusProvider.waitForRightsStateLoad(), this._stateStatusProvider.waitForBuildingStateLoad()]).then(
            async () => {
                // There are buildings
                if ((await firstValueFrom(this._buildingsProvider.getBuildingsCount$())) > 0) {
                    if (buildingId) {
                        this._injector.get(DashboardingProvider).setSelectedNodeId(buildingId as ClassificationNodeId);
                    }

                    // The user could have no current page (at url root) but we still should redirect him
                    const currentPage = await firstValueFrom(this.getCurrentPage$());

                    if (currentPage === Page.HOMEPAGE) {
                        return;
                    }
                    this.resetNavigationTo(Page.HOMEPAGE, {});
                }
                // No building
                else {
                    // Access to create a new building ?
                    if (this._profileProvider.hasFeatureAccess(CREATE_BUILDING_RIGHT_ACCESS)) {
                        this.resetNavigationTo(Page.BUILDING_CREATION, {});
                    } else {
                        // else user need to contact user projectAdmin, no access to this project
                        this.resetNavigationTo(Page.NO_BUILDING_ACCESS_ERROR, {});
                    }
                }
            }
        );
    }

    /**
     * @deprecated
     */
    replace<P extends Page>(page: P, pageParams: PageParams<P>): void {
        const dest = (routePaths[page].generator as any)(pageParams);
        this.getRouter().navigate(['/', ...dest.url], { queryParams: dest.query, replaceUrl: true });
    }

    /**
     * @deprecated
     */
    getCurrentPage$(): Observable<Page> {
        return this._currentPage$;
    }

    /**
     * @deprecated
     */
    listenToNavigationReset$() {
        return this._routeReseted$;
    }

    /**
     * @deprecated
     */
    notifyNavigationReset() {
        this._navigationCount$.next(0);
        this._routeReseted$.next();
    }

    getActiveRoutesMergedData$(): Observable<any> {
        return this._router$.pipe(
            filter(isNotNullish),
            switchMap(router => {
                return router.events.pipe(
                    filter(e => e instanceof NavigationEnd),
                    map(() => this.getCurrentActivatedRoutesMergedData(router)),
                    startWith(this.getCurrentActivatedRoutesMergedData(router))
                );
            })
        );
    }

    private getCurrentActivatedRoutesMergedData(router: Router): any {
        let r = router.routerState.root;
        const activatedSnapshots = [r.snapshot];
        while (r.firstChild) {
            r = r.firstChild;
            activatedSnapshots.push(r.snapshot);
        }
        return activatedSnapshots.reduce((d, snapshot) => {
            return {
                ...d,
                ...snapshot.data
            };
        }, {} as any);
    }

    static isRouteActivationEvent(ev: any): ev is ChildActivationEnd {
        return ev instanceof ChildActivationEnd && !!ev.snapshot.routeConfig;
    }

    static isNavigationEndEvent(ev: any): ev is NavigationEnd {
        return ev instanceof NavigationEnd;
    }
}
